/**
 * Decides whether to show the first-login voiceprint enrollment modal.
 *
 * Show when ALL of these hold:
 *   - the user is authenticated and has a clinical role that uses the scribe
 *     (doctor, nurse, etc. — we allow-list these; admin-only roles don't get
 *     prompted)
 *   - they have no active voiceprint for the `doctor` role
 *   - they haven't dismissed the prompt (`preferences.voiceprintPromptDismissedAt`
 *     is not set)
 *
 * Kept separate from the modal component so unit tests can drive the logic
 * without mounting UI.
 */

import { useMemo } from 'react';
import { useAuthStore } from '@/stores/useAuthStore';
import { useMyProfile } from './useMyProfile';
import { useMyVoiceprints } from './useVoiceprints';
import { useAiAvailability } from '@/config/aiCapabilities';

/**
 * Roles that see the voiceprint prompt. Keep this explicit — rolling it out
 * to every user type would be rude. Assistants are included because they run
 * sessions on behalf of doctors and diarization still benefits from speaker
 * identity.
 */
const PROMPTED_ROLES = new Set<string>(['doctor', 'nurse', 'assistant', 'physician']);

export interface VoiceprintOnboardingState {
  /** Final decision: should the modal render right now? */
  shouldPrompt: boolean;
  /** True while either the profile or voiceprint list is still loading. */
  loading: boolean;
  /** Derived explanations, useful for tests and debugging. */
  reasons: {
    authenticated: boolean;
    hasPromptedRole: boolean;
    hasActiveVoiceprint: boolean;
    /** False when the clinic's speech is routed away from our own service. */
    enrolmentAvailable: boolean;
    alreadyDismissed: boolean;
  };
}

export function useVoiceprintOnboarding(): VoiceprintOnboardingState {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const profile = useMyProfile(isAuthenticated);
  const voiceprints = useMyVoiceprints();
  // Enrolment needs the self-hosted speech service; a clinic routed to an
  // API can't complete it, so don't open with a prompt that dead-ends.
  const enrolmentAvailable = useAiAvailability('voiceprints').available;

  return useMemo(() => {
    const authenticated = !!isAuthenticated;
    const roleName =
      profile.data?.role?.name?.toLowerCase() ??
      // Fall back to useAuthStore's primaryRole if the profile fetch hasn't landed yet.
      (useAuthStore.getState().user as { role?: { name?: string } } | null)?.role?.name?.toLowerCase() ??
      '';
    const hasPromptedRole = authenticated && PROMPTED_ROLES.has(roleName);

    const hasActiveVoiceprint =
      Array.isArray(voiceprints.data) &&
      voiceprints.data.some((v) => v.role === 'doctor' && v.isActive);

    const alreadyDismissed =
      typeof profile.data?.preferences?.voiceprintPromptDismissedAt === 'string';

    const loading = profile.isLoading || voiceprints.isLoading;

    const shouldPrompt =
      authenticated &&
      enrolmentAvailable &&
      hasPromptedRole &&
      !hasActiveVoiceprint &&
      !alreadyDismissed &&
      !loading;

    return {
      shouldPrompt,
      loading,
      reasons: {
        authenticated,
        hasPromptedRole,
        hasActiveVoiceprint,
        alreadyDismissed,
        enrolmentAvailable,
      },
    };
  }, [
    isAuthenticated,
    enrolmentAvailable,
    profile.data,
    profile.isLoading,
    voiceprints.data,
    voiceprints.isLoading,
  ]);
}
